import { animated, config, useSpring } from "@react-spring/web";
import {
  Box,
  Flex,
  type FlexProps,
  Heading,
  IconButton,
} from "@sidelinesports/fan-design-system";
import { CancelIcon } from "@sidelinesports/icons";
import { clsx } from "clsx";
import React, {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementType,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useImpressionTracking } from "../../analytics/use-impression-tracking.ts";
import styles from "./Dialog.module.css";
import { useDialogFocus, useDialogKeyboardControls } from "./hooks";
import { Portal } from "./Portal";

interface DialogContextValue {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

interface DialogRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type DialogSize = "fullscreen" | "lg" | "md" | "sm";
type DialogGap = "sm" | "md" | "lg";

interface DialogContentProps {
  children: ReactNode;
  /** Name used for analytics tracking */
  name: string;
  size?: DialogSize;
  gap?: DialogGap;
  bg?: "primary" | "white";
  px?: FlexProps["px"];
  py?: FlexProps["py"];
  onFinishClose?: () => void;
}

interface DialogHeaderProps {
  children?: string | ReactNode;
  closeButton?: boolean | ReactNode;
}

interface DialogBodyProps {
  children: ReactNode;
}

interface DialogFooterProps {
  children?: ReactNode;
}

// Polymorphic CloseButton types
type PolymorphicProps<T extends ElementType> = {
  as?: T;
  children?: ReactNode;
} & ComponentPropsWithoutRef<T>;

type DialogTriggerProps<T extends ElementType = "button"> = PolymorphicProps<T>;
type CloseButtonProps<T extends ElementType = "button"> = PolymorphicProps<T>;

// Dialog context for sharing close function
const DialogContext = createContext<DialogContextValue | undefined>(undefined);

const useDialogContext = (): DialogContextValue => {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error("useDialogContext must be used within a Dialog component");
  }

  return context;
};

/**
 * The root component for the Dialog. It manages the open state and provides
 * context to its children.
 *
 * It can be controlled via props or managed internally via the `Dialog.Trigger`
 * and `Dialog.Close` components.
 */
const DialogRoot: React.FC<DialogRootProps> = ({
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const contextValue: DialogContextValue = {
    isOpen,
    onOpen: () => handleOpenChange(true),
    onClose: () => handleOpenChange(false),
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
    </DialogContext.Provider>
  );
};

// Trigger component
const DialogTrigger = <T extends ElementType = "button">({
  as,
  children,
  onClick,
  ...props
}: DialogTriggerProps<T>) => {
  const Component = as || "button";
  const { onOpen } = useDialogContext();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
    if (!e.defaultPrevented) {
      onOpen();
    }
  };

  return (
    <Component onClick={handleClick} {...props}>
      {children}
    </Component>
  );
};

const CloseButton = <T extends ElementType = "button">({
  as,
  children,
  onClick,
  ...props
}: CloseButtonProps<T>) => {
  const Component = as || DefaultCloseButton;
  const { onClose } = useDialogContext();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);

    if (!e.defaultPrevented) {
      onClose();
    }
  };

  return (
    <Component onClick={handleClick} {...props}>
      {children}
    </Component>
  );
};

const DefaultCloseButton = (props: CloseButtonProps) => (
  <CloseButton
    as={IconButton}
    variant="outline"
    size="lg"
    icon={<CancelIcon />}
    aria-label="Close"
    {...props}
  />
);

const DialogIllustration: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  return (
    <Flex className={styles.illustration} align="center" justify="center">
      {children}
    </Flex>
  );
};

const DialogHeader: React.FC<DialogHeaderProps> = ({
  children,
  closeButton = true,
}) => {
  const button =
    closeButton && typeof closeButton === "boolean" ? (
      <DefaultCloseButton />
    ) : (
      closeButton
    );

  return (
    <Flex align="center" justify="between">
      {children && (
        <Heading
          level={1}
          style={{
            textTransform: "uppercase",
          }}
        >
          {children}
        </Heading>
      )}
      {button}
    </Flex>
  );
};

const DialogBody: React.FC<DialogBodyProps> = ({ children }) => {
  return <Box>{children}</Box>;
};

// Footer component
const DialogFooter: React.FC<DialogFooterProps> = ({ children }) => {
  if (!children) return null;

  return (
    <Flex align="center" justify="center">
      {children}
    </Flex>
  );
};

const DialogContent: React.FC<DialogContentProps> = ({
  children,
  name,
  bg = "primary",
  size = "md",
  gap = "md",
  px,
  py,
  onFinishClose,
}) => {
  const { isOpen, onClose } = useDialogContext();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<Element | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useImpressionTracking("DIALOG_VIEW", {
    properties: { dialog: name },
    ref: dialogRef as React.RefObject<HTMLElement>,
  });

  // Track when we should render the component
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    }
  }, [isOpen]);

  // Spring animations for overlay and dialog
  const overlayAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    config: config.default,
    onRest: () => {
      // Only unmount after exit animation completes
      if (!isOpen) {
        setShouldRender(false);
        onFinishClose?.();
      }
    },
  });

  const dialogAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen
      ? "scale(1) translateY(0px)"
      : "scale(0.95) translateY(-10px)",
    config: config.default,
  });

  useDialogFocus({
    isOpen,
    dialogRef,
    previouslyFocusedElementRef: previouslyFocusedElement,
  });

  useDialogKeyboardControls({
    isOpen,
    dialogRef,
    onClose,
  });

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!shouldRender) return null;

  return (
    <Portal root={document.body}>
      <animated.div
        className={styles.overlay}
        style={overlayAnimation}
        onClick={handleOverlayClick}
        aria-hidden={!isOpen}
      >
        <AnimatedBox
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          bg={bg}
          p={16}
          px={px}
          py={py}
          tabIndex={-1}
          className={clsx(
            styles.dialog,
            styles[`size-${size}`],
            styles[`gap-${gap}`],
          )}
          style={dialogAnimation}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          {children}
        </AnimatedBox>
      </animated.div>
    </Portal>
  );
};

const AnimatedBox = animated(Box);

const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Content: DialogContent,
  Illustration: DialogIllustration,
  Header: DialogHeader,
  Body: DialogBody,
  Footer: DialogFooter,
  Close: CloseButton,
};

export { Dialog, useDialogContext };
